import type { billing, platform } from '@platform/contracts';
import { Badge } from '@platform/design-system';
import { TIER_LABEL } from './format.ts';

type Tone = 'success' | 'info' | 'warning' | 'error' | 'neutral';

/** Tenant and billing states as tinted tags (§6): sentence case, text always. */
const TENANT: Record<platform.TenantStatus, { tone: Tone; label: string }> = {
  pendingPayment: { tone: 'warning', label: 'Awaiting payment' },
  provisioning: { tone: 'info', label: 'Provisioning' },
  locked: { tone: 'warning', label: 'Locked' },
  active: { tone: 'success', label: 'Active' },
  suspended: { tone: 'warning', label: 'Suspended' },
  offboarding: { tone: 'neutral', label: 'Offboarding' },
  deprovisioned: { tone: 'neutral', label: 'Deprovisioned' },
  failed: { tone: 'error', label: 'Failed' },
};

const BILLING: Record<billing.SubscriptionStatus, { tone: Tone; label: string }> = {
  none: { tone: 'warning', label: 'Not started' },
  incomplete: { tone: 'warning', label: 'Payment pending' },
  paused: { tone: 'neutral', label: 'Paused' },
  trialing: { tone: 'info', label: 'Trial' },
  active: { tone: 'success', label: 'Billing active' },
  past_due: { tone: 'warning', label: 'Past due' },
  unpaid: { tone: 'error', label: 'Unpaid' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
};

export function TenantStatusBadge({ status }: { status: platform.TenantStatus }) {
  const s = TENANT[status] ?? { tone: 'neutral' as const, label: status };
  return <Badge tone={s.tone} label={s.label} />;
}

export function BillingStatusBadge({ status }: { status: billing.SubscriptionStatus }) {
  return <Badge tone={BILLING[status].tone} label={BILLING[status].label} />;
}

export function TierBadge({ tier }: { tier: platform.Tier }) {
  return <Badge label={TIER_LABEL[tier]} />;
}
